import { throwSecurityModuleMismatchError } from "../security-errors";
/** Converts a failed verification result into a thrown `SecurityModuleParameterMismatchError`. */
export function throwVerification(result, operation) {
    const error = throwSecurityModuleMismatchError(operation, {
        ok: false,
        message: result.message,
        ...(result.details ?? {}),
    }, result.message);
    error.decoded = result.decoded;
    throw error;
}
export function toBigIntSafe(value) {
    if (typeof value === "bigint")
        return value;
    if (typeof value === "number" && Number.isFinite(value))
        return BigInt(Math.trunc(value));
    if (typeof value === "string") {
        try {
            return BigInt(value);
        }
        catch {
            return undefined;
        }
    }
    return undefined;
}
export function verifyExpectedTransactionValueToTransfer(value, details) {
    if (value === 0n)
        return undefined;
    return {
        ok: false,
        message: `Unexpected Solana transaction value: expected 0, got ${value.toString()}`,
        details: {
            expectedValue: "0",
            actualValue: value.toString(),
            ...details,
        },
    };
}
export function verifyExpectedTransactionFeePayer(expectedFeePayer, actualFeePayer, details) {
    if (expectedFeePayer === actualFeePayer)
        return undefined;
    return {
        ok: false,
        message: `Fee payer mismatch: expected ${expectedFeePayer}, got ${actualFeePayer}`,
        details: {
            expectedFeePayer,
            actualFeePayer,
            ...details,
        },
    };
}
export function verifyExpectedSolanaStakeValidatorVotePubkey(expectedVotePubkey, actualVotePubkey, details) {
    if (actualVotePubkey === expectedVotePubkey)
        return undefined;
    return {
        ok: false,
        message: `Validator vote pubkey mismatch: expected ${expectedVotePubkey}, got ${actualVotePubkey}`,
        details: {
            expectedVotePubkey,
            actualVotePubkey,
            ...details,
        },
    };
}
export function verifyExpectedSolanaStakeAssetsFromMetadata(assets, metadata, details) {
    const minimumRentExemption = toBigIntSafe(metadata?.minimumRentExemption);
    const totalCost = toBigIntSafe(metadata?.totalCost);
    if (typeof minimumRentExemption === "undefined" || typeof totalCost === "undefined") {
        return undefined;
    }
    // totalCost = minimumRentExemption + stakeAmount + estimatedFee
    const estimatedFee = toBigIntSafe(metadata?.estimatedFee) ?? 0n;
    const actualStakeAmount = totalCost - minimumRentExemption - estimatedFee;
    if (actualStakeAmount === assets)
        return undefined;
    return {
        ok: false,
        message: `Stake amount mismatch: expected ${assets.toString()}, got ${actualStakeAmount.toString()}`,
        details: {
            expectedStakeAmount: assets.toString(),
            actualStakeAmount: actualStakeAmount.toString(),
            minimumRentExemption: minimumRentExemption.toString(),
            totalCost: totalCost.toString(),
            ...details,
        },
    };
}
//# sourceMappingURL=data:application/json;base64,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